import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { Play, Lock, Mail } from "lucide-react";

export default function AdminLogin() {
  const { admin, loginAdmin, API } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (admin) navigate("/admin", { replace: true });
  }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      loginAdmin(data);
      toast.success("Welcome back, Admin!");
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="visitor-gate-bg flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 mb-6">
            <Play className="w-8 h-8 text-[#FF5A00] fill-[#FF5A00]" />
            <span className="text-3xl font-bold tracking-tight text-[#0A1128]" style={{ fontFamily: 'Outfit' }}>
              HiFy
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0A1128] tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>
            Admin Login
          </h1>
          <p className="text-[#0A1128]/60 text-sm">
            Sign in to manage video content
          </p>
        </div>

        <div className="bg-white/95 backdrop-blur-xl p-8 md:p-10 rounded-2xl border border-[#0A1128]/10 shadow-[0_20px_60px_rgba(10,17,40,0.08)]">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold text-[#0A1128]/70 uppercase tracking-wider">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A1128]/30" />
                <Input
                  id="email"
                  type="email"
                  data-testid="admin-email-input"
                  placeholder="admin@hify.club"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="pl-10 h-12 bg-[#EAF0EC]/50 border-[#0A1128]/10 focus:border-[#FF5A00] focus:ring-[#FF5A00]/20 rounded-lg"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-semibold text-[#0A1128]/70 uppercase tracking-wider">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0A1128]/30" />
                <Input
                  id="password"
                  type="password"
                  data-testid="admin-password-input"
                  placeholder="Enter password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="pl-10 h-12 bg-[#EAF0EC]/50 border-[#0A1128]/10 focus:border-[#FF5A00] focus:ring-[#FF5A00]/20 rounded-lg"
                />
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-500" data-testid="login-error">{error}</p>
            )}

            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full h-12 bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold tracking-wide rounded-lg text-base transition-all shadow-sm mt-2"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
